import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Form, Radio } from 'antd';
import type { CSSProperties } from 'react';
import { CustomModal } from 'components/molecules/PricingSimulator/Modals/ScenarioColumnEditModal';
import useEventAttributeFilters from '../../../EventsPage/hooks/useEventAttributeFilters';
import type { UseDimOptionsHook } from '../../../../components/DimSelect';
import type { Labels } from '../../../../config/labels';
import type { OptimizePlanConfig } from '../../../../config/optimizePlanConfig';
import {
    TPO_OPTIMIZE_CONSTRAINTS_PANEL,
    TPO_OPTIMIZE_MODAL,
    TPO_OPTIMIZE_SCOPE_PANEL,
    TPO_OPTIMIZE_TAB_CONSTRAINTS,
    TPO_OPTIMIZE_TAB_SCOPE,
    TPO_OPTIMIZE_TABS,
} from '../../../../config/testIds';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';
import { usePlanDetailsContext } from '../../../context/PlanDetailsContext';
import type { RunOptimizationResponse } from '../../hooks/mutations/useRunOptimizationMutation';

import { useObjectiveOptions } from '../../hooks/queries/useObjectiveOptions';
import usePromotionCount from '../../hooks/queries/usePromotionCount';
import useConstraintsData from '../../hooks/queries/useConstraintsData';
import useRunOptimizationMutation from '../../hooks/mutations/useRunOptimizationMutation';
import { useOptimizeConstraints } from '../../hooks/ui/useOptimizeConstraints';
import { useOptimizePlanForm } from '../../hooks/ui/useOptimizePlanForm';
import { buildOptimizationPayload } from '../../utils/optimizePayloadBuilder';
import { queryClient } from 'services/reactQueryService';
import { TPOGEN_OPTIMIZE_STARTED } from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../../../utils/trackTpoGenEvent';

import ScopeTab from './ScopeTab';
import ConstraintsTab from './ConstraintsTab';
import OptimizePlanFooter from './OptimizePlanFooter';

/**
 * Adapts `useEventAttributeFilters` to the `UseDimOptionsHook` interface expected
 * by `ConfigDrivenFormFields` / `DimSelect`.  Mirrors the `useEventDimOptions`
 * pattern in EventsSection.tsx — defined at module level so the reference is
 * stable and never triggers unnecessary re-renders.
 */
const useOptimizeDimOptions: UseDimOptionsHook = (params) =>
    useEventAttributeFilters({
        dimension: params.dimension,
        attributeType: params.attributeType,
        lowestAttribute: params.lowestAttribute ?? false,
        filters: (params.filters ?? {}) as Record<string, unknown>,
        selectedDimension: params.selectedDimension ?? {
            value: '',
            dimension: params.dimension,
            attributeType: params.attributeType,
        },
        searchString: params.searchString ?? '',
        enabled: params.enabled,
    });

type ActiveTab = 'scope' | 'constraints';

type StatusMessageLabels = Labels['optimizePlan']['statusMessages'];

/** Context carried alongside the job id so the caller can attach it to the completed/failed analytics events. */
export interface OptimizationSubmitContext {
    objective: string | null;
    constraintCount: number;
}

interface Props {
    open: boolean;
    onCancel: () => void;
    onSubmitFinish: (data: RunOptimizationResponse, context: OptimizationSubmitContext) => void;
    planId: string;
    config: OptimizePlanConfig;
}

const MODAL_WIDTH = 940;

const MODAL_STYLE: CSSProperties = { borderRadius: 8 };
const MODAL_BODY_STYLE: CSSProperties = { height: 560, overflow: 'hidden' };

const resolveStatusMessage = (
    labels: StatusMessageLabels,
    state: { submitting: boolean; blocked: boolean; constraintsLoading: boolean; promotionCountLoading: boolean },
): string | undefined => {
    if (state.submitting) return labels.startingOptimization;
    if (state.blocked) return undefined;
    if (state.constraintsLoading) return labels.loadingConstraints;
    if (state.promotionCountLoading) return labels.countingPromotions;
    return undefined;
};

const OptimizePlanModal: React.FC<Props> = ({ open, onCancel, onSubmitFinish, planId, config }) => {
    const tpoGenConfig = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    const planYear = currentPlanDetails?.plan_year;
    const [activeTab, setActiveTab] = useState<ActiveTab>('scope');
    const [form] = Form.useForm();

    // --- UI hook (pure client state + derivations) ---
    const {
        formData,
        setFormData,
        level,
        setLevel,
        levelOptions,
        scopeSelections,
        scopeFilters,
        promotionCountStartDate,
        requiredFormDataPayload,
        resetForm,
    } = useOptimizePlanForm({ form, planId, config });

    // --- Queries ---
    const { objectiveList, objectiveListLoading, error: objectiveListQueryError } = useObjectiveOptions(open);
    const objectiveListError = objectiveListQueryError != null;

    const { promotionTotalCount, promotionCountLoading } = usePromotionCount({
        planId,
        filters: scopeFilters,
        startDate: promotionCountStartDate,
        enabled: open,
    });

    const { constraintsData, constraintsLoading, constraintsError } = useConstraintsData({
        payload: requiredFormDataPayload,
        enabled: Boolean(requiredFormDataPayload),
    });

    // --- Mutation ---
    const { mutate: runOptimizationMutate, isPending: runOptimizationLoading } = useRunOptimizationMutation();

    // --- UI hook (constraint table state) ---
    const { tableData, setTableData, kpiList, errorExists, onKPISelectHandler, resetConstraintTable } =
        useOptimizeConstraints({
            constraintsData,
            constraintsError,
            level,
            config,
            objectiveList,
            objectiveKey: formData.objective,
            startDate: formData.optimization_start_date,
            open,
        });

    // --- Effects (transferred verbatim from useOptimizePlan) ---

    // Reset keep_top whenever the available promotion pool changes.
    const prevPromotionTotalCountRef = useRef(promotionTotalCount);
    useEffect(() => {
        if (prevPromotionTotalCountRef.current !== promotionTotalCount) {
            prevPromotionTotalCountRef.current = promotionTotalCount;
            setFormData((prev) => ({ ...prev, keep_top: null }));
        }
    }, [promotionTotalCount, setFormData]);

    useEffect(() => {
        if (!open) {
            queryClient.removeQueries({ queryKey: ['tpo-gen', 'promotion-count'] });
        }
    }, [open]);

    useEffect(() => {
        if (open) {
            queryClient.removeQueries({ exact: false, queryKey: ['tpo-gen', 'optimization-org-values'] });
        }
    }, [open]);

    // --- Composed resets & handlers ---
    const resetAll = useCallback(() => {
        resetForm();
        resetConstraintTable();
    }, [resetForm, resetConstraintTable]);

    const handleCancel = useCallback(() => {
        resetAll();
        onCancel();
    }, [resetAll, onCancel]);

    const handleModalCancel = useCallback(() => {
        handleCancel();
        setActiveTab('scope');
    }, [handleCancel]);

    const handleSubmit = useCallback(() => {
        const apiPayload = buildOptimizationPayload({
            planId,
            formData,
            tableData,
            level,
            scopeSelections,
            baseInputFields: config.baseInputFields,
            limitConstraintFields: config.limitConstraintFields,
        });

        trackTpoGenEvent(TPOGEN_OPTIMIZE_STARTED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_CALENDAR, {
            plan_id: planId,
            objective: formData.objective,
            constraint_count: tableData.length,
        });

        runOptimizationMutate(apiPayload, {
            onSuccess: (data: RunOptimizationResponse) => {
                resetAll();
                onSubmitFinish(data, { objective: formData.objective, constraintCount: tableData.length });
            },
        });
    }, [
        planId,
        formData,
        tableData,
        level,
        scopeSelections,
        config.baseInputFields,
        config.limitConstraintFields,
        runOptimizationMutate,
        resetAll,
        onSubmitFinish,
    ]);

    const hasMissingRequiredFields = config.baseInputFields.some(
        (f) => f.required && f.name != null && formData[f.name] == null,
    );
    const isSubmitDisabled = errorExists || hasMissingRequiredFields;

    const statusMessage = resolveStatusMessage(tpoGenConfig.labels.optimizePlan.statusMessages, {
        submitting: runOptimizationLoading,
        blocked: isSubmitDisabled,
        constraintsLoading,
        promotionCountLoading,
    });
    const isRunBlocked = isSubmitDisabled || statusMessage != null;

    const modalTitle = (
        <div className="flex items-center gap-4">
            <span className="text-base font-medium text-[#18181b]">{tpoGenConfig.labels.optimizePlan.modalTitle}</span>
            <Radio.Group
                value={activeTab}
                onChange={(e) => setActiveTab(e.target.value)}
                className="flex"
                data-testid={TPO_OPTIMIZE_TABS}
            >
                <Radio.Button
                    value="scope"
                    className="!rounded-l-md !rounded-r-none"
                    data-testid={TPO_OPTIMIZE_TAB_SCOPE}
                    data-state-active={activeTab === 'scope' ? 'true' : undefined}
                >
                    {tpoGenConfig.labels.optimizePlan.tabs.scope}
                </Radio.Button>
                <Radio.Button
                    value="constraints"
                    className="!rounded-r-md !rounded-l-none"
                    data-testid={TPO_OPTIMIZE_TAB_CONSTRAINTS}
                    data-state-active={activeTab === 'constraints' ? 'true' : undefined}
                >
                    {tpoGenConfig.labels.optimizePlan.tabs.constraints}
                </Radio.Button>
            </Radio.Group>
        </div>
    );

    return (
        <CustomModal
            style={MODAL_STYLE}
            bodyStyle={MODAL_BODY_STYLE}
            width={MODAL_WIDTH}
            footer={
                <OptimizePlanFooter
                    statusMessage={statusMessage}
                    cancelLabel={tpoGenConfig.labels.common.cancel}
                    runLabel={tpoGenConfig.labels.optimizePlan.runOptimisation}
                    runDisabled={isRunBlocked}
                    onCancel={handleModalCancel}
                    onRun={handleSubmit}
                />
            }
            maskClosable={false}
            destroyOnClose
            centered
            open={open}
            onCancel={handleModalCancel}
            title={modalTitle}
        >
            <Form form={form} layout="vertical" style={{ height: '100%' }} data-testid={TPO_OPTIMIZE_MODAL}>
                <div className="h-full flex flex-col overflow-hidden">
                    <div
                        className={activeTab === 'scope' ? 'h-full' : 'hidden'}
                        data-testid={TPO_OPTIMIZE_SCOPE_PANEL}
                        data-state-active={activeTab === 'scope' ? 'true' : undefined}
                    >
                        <ScopeTab
                            scopeOfOptimisationFields={config.scopeOfOptimisationFields}
                            baseInputFields={config.baseInputFields}
                            useDimOptions={useOptimizeDimOptions}
                            formData={formData}
                            setFormData={setFormData}
                            objectiveList={objectiveList}
                            objectiveListLoading={objectiveListLoading}
                            objectiveListError={objectiveListError}
                            promotionTotalCount={promotionTotalCount}
                            promotionCountLoading={promotionCountLoading}
                            planYear={planYear}
                        />
                    </div>
                    <div
                        className={activeTab === 'constraints' ? 'h-full' : 'hidden'}
                        data-testid={TPO_OPTIMIZE_CONSTRAINTS_PANEL}
                        data-state-active={activeTab === 'constraints' ? 'true' : undefined}
                    >
                        <ConstraintsTab
                            level={level}
                            setLevel={setLevel}
                            levelOptions={levelOptions}
                            tableData={tableData}
                            setTableData={setTableData}
                            tableHeaders={config.constraintTableHeaders}
                            kpiList={kpiList}
                            onKPISelectHandler={onKPISelectHandler}
                            errorExists={errorExists}
                            constraintsLoading={constraintsLoading}
                        />
                    </div>
                </div>
            </Form>
        </CustomModal>
    );
};

OptimizePlanModal.displayName = 'OptimizePlanModal';
export default OptimizePlanModal;
